<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import OrgTreePicker from '@/components/OrgTreePicker.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';
import { personLabel } from '@/utils/person';

/**
 * 团队页的快捷添加（0904 Tony：「增减成员要更便捷」）。
 *
 * 原来加一个人要走通用记录表单：四个字段、一棵组织树、一个 109 项的原生下拉，
 * 而真正要定的只有两件事——**谁**、**担什么角色**。姓名和邮箱都是选完人自动带的。
 * 这里只留这两格；从空岗卡片进来时角色已经填好，只剩选人。
 *
 * 加完默认关掉；项目刚建团队要连着加好几个人，所以另给一个「加入并继续」。
 */
export interface TeamPerson { id: number; displayName: string; orgName: string | null; email: string | null }
export interface TeamRoleOption { role: string; inApproval: boolean; memberCount: number }

const props = defineProps<{
  open: boolean;
  projectId: string | number;
  people: TeamPerson[];
  roles: TeamRoleOption[];
  /** 已在项目里的成员，用来拦「同一个人同一个角色加两遍」 */
  members: ModuleRecord[];
  /** 从空岗卡片进来时预选的角色 */
  presetRole?: string;
  /** 从某个职能组的「＋」进来时：这一组的角色排在最前 */
  groupLabel?: string;
  groupMatch?: RegExp | null;
  /** 换人：被替换的那条成员记录（已离职），新人加入后把它移出 */
  replacing?: { id: string | number; name: string; role: string };
}>();
const emit = defineEmits<{ close: []; added: [] }>();

const CUSTOM = '__custom__';

const personId = ref<number | null>(null);
/** 从部门树点来的人可能不在通讯录快照里（通讯录没读到时），单独记一份 */
const orgPicked = ref<TeamPerson | null>(null);
const role = ref('');
const customRole = ref('');
const orgOpen = ref(false);
const saving = ref(false);
const pickerKey = ref(0);
const lastAdded = ref('');

watch(() => props.open, (open) => {
  if (!open) return;
  personId.value = null;
  orgPicked.value = null;
  role.value = props.presetRole ?? props.replacing?.role ?? '';
  customRole.value = '';
  orgOpen.value = false;
  lastAdded.value = '';
  pickerKey.value += 1;
}, { immediate: true });

const person = computed<TeamPerson | null>(() => {
  if (personId.value === null) return null;
  return props.people.find((item) => item.id === personId.value)
    ?? (orgPicked.value?.id === personId.value ? orgPicked.value : null);
});

/** 预选的角色不在选项里（写法有出入）时也要能选到，单独挂一组 */
const pinnedRoles = computed(() => (
  props.presetRole && !props.roles.some((item) => item.role === props.presetRole)
    ? [props.presetRole]
    : []
));
const groupRoles = computed(() => (props.groupMatch
  ? props.roles.filter((item) => props.groupMatch!.test(item.role))
  : []));
const approvalRoles = computed(() => props.roles.filter((item) =>
  item.inApproval && !groupRoles.value.includes(item)));
const otherRoles = computed(() => props.roles.filter((item) =>
  !item.inApproval && !groupRoles.value.includes(item)));

const finalRole = computed(() => (role.value === CUSTOM ? customRole.value : role.value).trim());

/** 和后端 ApprovalService.normalizeRole 同口径的粗归一：空格、喷涂/喷漆 */
function normalizeRole(value: string): string {
  return value.replace(/\s+/gu, '').replaceAll('喷涂', '喷漆');
}

/** 这个人已经在本项目里担什么 */
const existingRoles = computed(() => props.members
  .filter((member) => Number(member.parent_id ?? member.parentId) === personId.value)
  .map((member) => String(member.code ?? member.roleTitle ?? '').trim())
  .filter(Boolean));

const duplicate = computed(() => Boolean(
  finalRole.value
  && existingRoles.value.some((item) => normalizeRole(item) === normalizeRole(finalRole.value)),
));

const canSubmit = computed(() =>
  person.value !== null && finalRole.value !== '' && !duplicate.value && !saving.value);

function onOrgPicked(
  userId: number | null,
  member: { id: number; displayName: string; email: string | null } | null,
): void {
  if (!userId) return;
  personId.value = userId;
  if (member) {
    orgPicked.value = { id: member.id, displayName: member.displayName, orgName: null, email: member.email };
  }
  orgOpen.value = false;
}

function onUnresolved(text: string): void {
  notify('error', '这个名字对不上账号',
    `「${text}」在通讯录里找不到唯一的人。请从下拉建议里选，或用「按部门找人」。`);
}

async function submit(keepOpen: boolean): Promise<void> {
  const picked = person.value;
  if (!canSubmit.value || !picked) return;
  saving.value = true;
  try {
    const payload: Record<string, unknown> = {
      parentId: picked.id,
      code: finalRole.value,
      title: picked.displayName,
    };
    if (picked.email) payload.ownerName = picked.email;
    const created = await eqsApi.createProjectResource('hr', 'project-members', props.projectId, payload);
    // 加了人，这个角色的阶段任务后端当场按角色落了负责人（0929），落了几条说出来
    const assigned = Number((created as { ownerSync?: { assigned?: number } }).ownerSync?.assigned ?? 0);
    const ownerNote = assigned > 0 ? `；按角色给 ${assigned} 个阶段任务落了负责人` : '';
    if (props.replacing) {
      // 换人：新人进来了才移旧的，顺序反过来会出现「角色一时没人」的空档
      try {
        const open = await eqsApi.teamMemberOpenItems(props.projectId, props.replacing.id);
        if (!open.canRemove) {
          notify(
            'error',
            '新人已加入，但旧成员仍有未交接工作',
            `${props.replacing.name} 还有 ${open.hard.length} 项硬闸事项（阶段任务/审批/派活/有账号交付物）。`
              + '请到团队卡片点「移出」完成交接后再移出旧成员。',
          );
          emit('added');
          emit('close');
          return;
        }
        await eqsApi.removeProjectMember(props.replacing.id);
        notify('success', '已换人', `${picked.displayName} 接替 ${props.replacing.name} · ${finalRole.value}`
          + '；这个角色的阶段任务会跟着换到新人名下');
      } catch (caught) {
        notify('error', '新人已加入，但移出旧成员失败',
          `${props.replacing.name} 还在名单里，请在卡片上手动移出。${errorMessage(caught)}`);
      }
      emit('added');
      emit('close');
      return;
    }
    notify('success', '已加入项目', `${picked.displayName} · ${finalRole.value}${ownerNote}`);
    emit('added');
    if (!keepOpen) {
      emit('close');
      return;
    }
    lastAdded.value = `${picked.displayName} · ${finalRole.value}`;
    personId.value = null;
    orgPicked.value = null;
    pickerKey.value += 1;
  } catch (caught) {
    notify('error', '加入失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open && !saving.value) emit('close');
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="open" class="qa-mask" @click.self="!saving && emit('close')">
    <section class="qa-dialog" role="dialog" aria-modal="true" aria-labelledby="qa-title">
      <header>
        <div>
          <span class="qa-kicker">TEAM / ADD</span>
          <h3 id="qa-title">{{ replacing ? '换人' : '添加团队成员' }}</h3>
          <p v-if="replacing">
            {{ replacing.name }} 已离职。给「{{ replacing.role }}」选一个新人，加入后会把他从本项目移出；
            他名下的任务责任人不会自动改，请另行安排。
          </p>
          <p v-else-if="presetRole">
            给「{{ presetRole }}」安排人。这个角色是本项目审批流点名要的，配上之后审批不用再借别的项目的人。
          </p>
          <p v-else-if="groupLabel">往「{{ groupLabel }}」里加人；角色下拉把这一组的排在最前面。</p>
          <p v-else>选一个人、定一个角色就行。加入后他会收到站内通知，本项目的待办和审批也会派给他。</p>
        </div>
        <button class="inline-button" type="button" :disabled="saving" @click="emit('close')">关闭</button>
      </header>

      <form @submit.prevent="submit(false)">
        <div class="qa-field">
          <span class="qa-label">谁 *</span>
          <div class="qa-person-row">
            <PersonPicker
              :key="pickerKey"
              :people="people"
              :model-value="personId"
              label="搜索姓名"
              placeholder="输入姓名，从建议里选"
              list-id="team-quick-add-people"
              @update:model-value="personId = $event"
              @unresolved="onUnresolved"
            />
            <button type="button" class="inline-button" @click="orgOpen = !orgOpen">
              {{ orgOpen ? '收起部门树' : '按部门找人' }}
            </button>
          </div>
          <datalist id="team-quick-add-people">
            <option v-for="item in people" :key="item.id" :value="personLabel(item)" />
          </datalist>
          <small v-if="!people.length" class="qa-warn">通讯录还没读到。可以用「按部门找人」，或者关掉重开。</small>
          <OrgTreePicker v-if="orgOpen" :model-value="personId" @update:model-value="onOrgPicked" />
          <div v-if="person" class="qa-picked">
            <b>{{ person.displayName }}</b>
            <span>{{ person.orgName || '未挂部门' }} · {{ person.email || '账号没有邮箱，只收站内通知' }}</span>
            <span v-if="existingRoles.length" class="qa-note">已在本项目担任：{{ existingRoles.join('、') }}</span>
          </div>
        </div>

        <div class="qa-field">
          <label class="qa-label" for="qa-role">担任角色 *</label>
          <select id="qa-role" v-model="role">
            <option value="">请选择</option>
            <optgroup v-if="pinnedRoles.length" label="空岗角色">
              <option v-for="item in pinnedRoles" :key="item" :value="item">{{ item }}</option>
            </optgroup>
            <optgroup v-if="groupRoles.length" :label="`「${groupLabel}」这一组的角色`">
              <option v-for="item in groupRoles" :key="item.role" :value="item.role">{{ item.role }}</option>
            </optgroup>
            <optgroup v-if="approvalRoles.length" label="审批流会用到的角色">
              <option v-for="item in approvalRoles" :key="item.role" :value="item.role">{{ item.role }}</option>
            </optgroup>
            <optgroup v-if="otherRoles.length" label="其他在用角色">
              <option v-for="item in otherRoles" :key="item.role" :value="item.role">{{ item.role }}</option>
            </optgroup>
            <option :value="CUSTOM">其他角色（手工填写）</option>
          </select>
          <input
            v-if="role === CUSTOM"
            v-model="customRole"
            class="qa-custom"
            type="text"
            placeholder="例：包装工程师"
            aria-label="手工填写角色"
          >
          <small v-if="duplicate" class="qa-error">
            {{ person?.displayName }} 已经是本项目的「{{ finalRole }}」，不用再加。
          </small>
          <small v-else-if="role === CUSTOM" class="qa-warn">
            手工填的角色审批流认不到；写法要和审批流里完全一致才派得到人。
          </small>
          <small v-else class="qa-hint">审批和评审按角色名精确找人；选「审批流会用到的角色」才收得到审批任务。</small>
        </div>

        <p v-if="lastAdded" class="qa-done">已加入 {{ lastAdded }}。接着选下一个人，或者关闭。</p>

        <footer>
          <button class="button button-primary" type="submit" :disabled="!canSubmit">
            {{ saving ? (replacing ? '正在换人…' : '正在加入…') : replacing ? '换人' : '加入项目' }}
          </button>
          <button v-if="!replacing" class="button button-secondary" type="button" :disabled="!canSubmit" @click="submit(true)">
            加入并继续添加
          </button>
          <button class="button" type="button" :disabled="saving" @click="emit('close')">取消</button>
        </footer>
      </form>
    </section>
  </div>
</template>

<style scoped>
.qa-mask { position: fixed; inset: 0; z-index: 60; display: flex; align-items: flex-start; justify-content: center; padding: 6vh 16px; background: rgba(15, 23, 36, .45); overflow-y: auto; }
.qa-dialog { width: min(620px, 100%); border: 1px solid var(--line); border-radius: var(--radius); background: var(--white); box-shadow: 0 18px 48px rgba(15, 23, 36, .22); }
.qa-dialog > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.qa-kicker { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.qa-dialog header h3 { margin: 3px 0 0; font-size: 18px; }
.qa-dialog header p { margin: 4px 0 0; max-width: 52ch; color: var(--muted); font-size: 13px; line-height: 1.6; }
.qa-dialog form { display: grid; gap: 16px; padding: 16px 20px; }
.qa-field { display: grid; gap: 6px; }
.qa-label { font-size: 13px; font-weight: 700; }
.qa-person-row { display: flex; gap: 8px; align-items: center; }
.qa-person-row :deep(.person-picker) { flex: 1; min-width: 0; min-height: 36px; padding: 6px 10px; border: 1px solid var(--line-strong); background: var(--white); font: inherit; font-size: 13px; }
.qa-dialog select, .qa-custom { width: 100%; min-height: 36px; padding: 6px 10px; border: 1px solid var(--line-strong); background: var(--white); font: inherit; font-size: 13px; }
.qa-picked { display: grid; gap: 2px; padding: 9px 12px; border: 1px solid var(--line); border-left: 3px solid var(--blue); border-radius: var(--radius-s); background: var(--paper); }
.qa-picked b { font-size: 14px; }
.qa-picked span { color: var(--muted); font-size: 12px; }
.qa-picked .qa-note { color: var(--warning); }
.qa-hint { color: var(--muted); font-size: 12px; }
.qa-warn { color: var(--warning); font-size: 12px; }
.qa-error { color: var(--risk); font-size: 12px; font-weight: 700; }
.qa-done { margin: 0; padding: 8px 12px; border-left: 3px solid var(--green); border-radius: 0 var(--radius-s) var(--radius-s) 0; background: rgba(38, 122, 99, .08); color: var(--green); font-size: 13px; }
.qa-dialog footer { display: flex; flex-wrap: wrap; gap: 8px; }

@media (max-width: 600px) {
  .qa-mask { padding: 0; }
  .qa-dialog { min-height: 100%; border-radius: 0; }
  .qa-person-row { flex-direction: column; align-items: stretch; }
}
</style>
